/**
 * Surebet Types and Constants
 * Based on MarketsAnalysisDto structure from backend
 */

/**
 * Market Type Mapping
 * Maps backend margin keys to user-friendly names and configurations
 */
export const MARGIN_TYPES = {
  margin1X2: {
    key: 'margin1X2',
    requiredCoefficients: ['Coef1', 'CoefX', 'Coef2'],
    description: {
      bg: 'Краен резултат',
      en: 'Match Result'
    },
    outcomes: {
      Coef1: { bg: 'Домакин Победа', en: 'Home Win' },
      CoefX: { bg: 'Равенство', en: 'Draw' },
      Coef2: { bg: 'Гост Победа', en: 'Away Win' }
    }
  },

  marginOverUnder2AndHalf: {
    key: 'marginOverUnder2AndHalf',
    requiredCoefficients: ['CoefGoalOver2AndHalf', 'CoefGoalUnder2AndHalf'],
    description: {
      bg: 'Над/Под 2.5 гола',
      en: 'Over/Under 2.5 Goals'
    },
    outcomes: {
      CoefGoalOver2AndHalf: { bg: 'Над 2.5', en: 'Over 2.5' },
      CoefGoalUnder2AndHalf: { bg: 'Под 2.5', en: 'Under 2.5' }
    }
  },

  marginBothScore: {
    key: 'marginBothScore',
    requiredCoefficients: ['CoefBothScoreYes', 'CoefBothScoreNo'],
    description: {
      bg: 'Двата отбора ще вкарат',
      en: 'Both Teams to Score'
    },
    outcomes: {
      CoefBothScoreYes: { bg: 'Да', en: 'Yes' },
      CoefBothScoreNo: { bg: 'Не', en: 'No' }
    }
  },

  // Used by Market Anomalies (Best Odds). Arbitrage surebets use cross-market
  // pairs (margin1Xvs2 / marginX2vs1 / margin12vsX) instead of this 3-way key.
  marginDoubleChance: {
    key: 'marginDoubleChance',
    requiredCoefficients: ['Coef1X', 'CoefX2', 'Coef12'],
    description: {
      bg: 'Двоен шанс',
      en: 'Double Chance'
    },
    outcomes: {
      Coef1X: { bg: 'Домакин Победа или Равен', en: 'Home Win or Draw' },
      CoefX2: { bg: 'Равен или Гост Победа', en: 'Draw or Away Win' },
      Coef12: { bg: 'Домакин Победа или Гост Победа', en: 'Home Win or Away Win' }
    }
  },

  margin1Xvs2: {
    key: 'margin1Xvs2',
    requiredCoefficients: ['Coef1X', 'Coef2'],
    description: {
      bg: '1X срещу 2',
      en: '1X vs 2'
    },
    outcomes: {
      Coef1X: { bg: '1X', en: '1X' },
      Coef2: { bg: 'Гост Победа', en: 'Away Win' }
    }
  },

  marginX2vs1: {
    key: 'marginX2vs1',
    requiredCoefficients: ['CoefX2', 'Coef1'],
    description: {
      bg: 'X2 срещу 1',
      en: 'X2 vs 1'
    },
    outcomes: {
      CoefX2: { bg: 'X2', en: 'X2' },
      Coef1: { bg: 'Домакин Победа', en: 'Home Win' }
    }
  },

  margin12vsX: {
    key: 'margin12vsX',
    requiredCoefficients: ['Coef12', 'CoefX'],
    description: {
      bg: '12 срещу X',
      en: '12 vs X'
    },
    outcomes: {
      Coef12: { bg: '12', en: '12' },
      CoefX: { bg: 'Равенство', en: 'Draw' }
    }
  },

  marginOverUnder1AndHalf: {
    key: 'marginOverUnder1AndHalf',
    requiredCoefficients: ['CoefGoalOver1AndHalf', 'CoefGoalUnder1AndHalf'],
    description: {
      bg: 'Над/Под 1.5 гола',
      en: 'Over/Under 1.5 Goals'
    },
    outcomes: {
      CoefGoalOver1AndHalf: { bg: 'Над 1.5', en: 'Over 1.5' },
      CoefGoalUnder1AndHalf: { bg: 'Под 1.5', en: 'Under 1.5' }
    }
  },

  marginOverUnder3AndHalf: {
    key: 'marginOverUnder3AndHalf',
    requiredCoefficients: ['CoefGoalOver3AndHalf', 'CoefGoalUnder3AndHalf'],
    description: {
      bg: 'Над/Под 3.5 гола',
      en: 'Over/Under 3.5 Goals'
    },
    outcomes: {
      CoefGoalOver3AndHalf: { bg: 'Над 3.5', en: 'Over 3.5' },
      CoefGoalUnder3AndHalf: { bg: 'Под 3.5', en: 'Under 3.5' }
    }
  }
};

/**
 * Bookmaker Names
 * Licensed Bulgarian bookmakers
 */
export const BOOKMAKERS = {
  betano: {
    name: 'Betano',
    logo: '/betano-logo-orange.png'
  },
  bet365: {
    name: 'Bet365',
    logo: '/bet365.png'
  },
  winbet: {
    name: 'Winbet',
    logo: '/winbet.png'
  },
  betwild: {
    name: 'Betwild',
    logo: '/betwild.png'
  },
  slybet: {
    name: 'Slybet',
    logo: '/slybet-logo.png'
  },
  bwin: {
    name: 'Bwin',
    logo: '/bwin-black-logo.png'
  },
  efbet: {
    name: 'Efbet',
    logo: '/efbet-logo-grey.png'
  },
  inbet: {
    name: 'Inbet',
    logo: '/inbet-logo.png'
  },
  alphawin: {
    name: 'Alphawin',
    logo: '/alphawin-blue.png'
  },
  sesame: {
    name: 'Sesame',
    logo: '/sesame-bg-logo.png'
  },
  elitbet: {
    name: 'Elitbet',
    logo: '/elitbet-black-logo.png'
  },
  bethub: {
    name: 'Bethub',
    logo: '/bethub-black.png'
  },
  bet: {
    name: 'Bet.bg',
    logo: '/bet-bg-black.png'
  },
  topwin: {
    name: 'Topwin',
    logo: '/topwin-red-logo.png'
  },
  mrbit: {
    name: 'MrBit',
    logo: '/mrbit.png'
  },
  admiralbet: {
    name: 'Admiralbet',
    logo: '/admiral-bet-logo.png'
  },
  bg8888: {
    name: '8888.bg',
    logo: '/8888-logo-blue.png'
  },
  palmsbet: {
    name: 'Palmsbet',
    logo: '/palms-bet-logo.png'
  },
  everbet: {
    name: 'Everbet',
    logo: '/everbet-blue-logo.png'
  },
  magicbet: {
    name: 'Magicbet',
    logo: '/magicbet.png'
  },
  betvam: {
    name: 'Betvam',
    logo: '/betvam-logo.png'
  },
  qbet: {
    name: 'Qbet',
    logo: '/qbet-logo.png'
  },
  imperiabet: {
    name: 'Imperiabet',
    logo: '/imperiabet-logo.png'
  }
};

/**
 * Sort Options
 */
export const SORT_OPTIONS = {
  MARGIN_DESC: { value: 'margin', order: 'desc', label: { bg: 'Марж (намаляващ)', en: 'Margin (descending)' } },
  MARGIN_ASC: { value: 'margin', order: 'asc', label: { bg: 'Марж (нарастващ)', en: 'Margin (ascending)' } },
  DATE_DESC: { value: 'date', order: 'desc', label: { bg: 'Дата (нова към стара)', en: 'Date (newest first)' } },
  DATE_ASC: { value: 'date', order: 'asc', label: { bg: 'Дата (стара към нова)', en: 'Date (oldest first)' } },
  TIME_DESC: { value: 'time', order: 'desc', label: { bg: 'Време (по-късно)', en: 'Time (later)' } },
  TIME_ASC: { value: 'time', order: 'asc', label: { bg: 'Време (по-рано)', en: 'Time (earlier)' } }
};

/**
 * Refresh Configuration
 */
export const REFRESH_CONFIG = {
  AUTO_REFRESH_INTERVAL: 600000, // 10 minutes in milliseconds
  MIN_REFRESH_INTERVAL: 60000, // 1 minute minimum between manual refreshes
  MAX_RETRIES: 3,
  RETRY_DELAY: 2000 // 2 seconds
};

/**
 * Data Validation Rules
 */
export const VALIDATION_RULES = {
  MIN_MARGIN: -100, // Minimum acceptable margin percentage
  MAX_MARGIN: 100000, // Backend may send large values for some market types (e.g. single-coefficient margins), so allow wide range
  MIN_COEFFICIENT: 1.01, // Minimum acceptable coefficient value
  MAX_COEFFICIENT: 1000, // Maximum acceptable coefficient value
  REQUIRED_EVENT_FIELDS: ['id', 'date', 'time', 'teamOne', 'teamTwo', 'championship', 'sport'],
  REQUIRED_MARGIN_FIELDS: ['value', 'coefficients'],
  REQUIRED_COEFFICIENT_FIELDS: ['bookmaker', 'type', 'value']
};

/**
 * Error Messages
 */
export const ERROR_MESSAGES = {
  FETCH_FAILED: {
    bg: 'Неуспешно зареждане на данни за арбитражи',
    en: 'Failed to fetch surebet data'
  },
  INVALID_DATA: {
    bg: 'Невалидни данни за арбитражи',
    en: 'Invalid surebet data'
  },
  NO_DATA: {
    bg: 'Няма налични арбитражни възможности в момента',
    en: 'No arbitrage opportunities available at the moment'
  },
  NETWORK_ERROR: {
    bg: 'Грешка в мрежата. Моля, проверете връзката си',
    en: 'Network error. Please check your connection'
  },
  SUBSCRIPTION_REQUIRED: {
    bg: 'Необходим е активен абонамент за достъп до арбитражните възможности',
    en: 'Active subscription required to access arbitrage opportunities'
  },
  RATE_LIMIT: {
    bg: 'Твърде много опити за обновяване. Моля, изчакайте малко',
    en: 'Too many refresh attempts. Please wait a moment'
  }
};

/**
 * Success Messages
 */
export const SUCCESS_MESSAGES = {
  DATA_LOADED: {
    bg: 'Данните са заредени успешно',
    en: 'Data loaded successfully'
  },
  DATA_REFRESHED: {
    bg: 'Данните са обновени успешно',
    en: 'Data refreshed successfully'
  }
};

/**
 * Loading States
 */
export const LOADING_STATES = {
  IDLE: 'idle',
  LOADING: 'loading',
  REFRESHING: 'refreshing',
  SUCCESS: 'success',
  ERROR: 'error'
};

/**
 * Default MarketsAnalysisDto Structure
 * For reference and validation
 */
export const MARKETS_ANALYSIS_DTO_STRUCTURE = {
  id: 0, // number
  event: {
    id: 0, // number
    date: '', // string (YYYY-MM-DD)
    time: '', // string (HH:MM)
    teamOne: '', // string
    teamTwo: '', // string
    championship: '', // string
    sport: '' // string
  },
  marginFlag: false, // boolean (true if any margin > 0)
  margins: {
    // Dynamic object - only includes margin types with complete data
    // Example structure:
    // margin1X2: {
    //   value: 0, // number (margin percentage)
    //   coefficients: [
    //     {
    //       bookmaker: '', // string
    //       type: '', // string (e.g., 'Coef1', 'CoefX', 'Coef2')
    //       value: 0, // number (decimal odds)
    //     }
    //   ]
    // }
  }
};

export default {
  MARGIN_TYPES,
  BOOKMAKERS,
  SORT_OPTIONS,
  REFRESH_CONFIG,
  VALIDATION_RULES,
  ERROR_MESSAGES,
  SUCCESS_MESSAGES,
  LOADING_STATES,
  MARKETS_ANALYSIS_DTO_STRUCTURE
};

